/**
 * Environment Configuration for CloudFlare Pages and Local Development
 *
 * This file provides a unified way to access environment variables
 * that works with both local development and CloudFlare Pages deployment.
 *
 * CloudFlare Pages will use the REACT_APP_ prefixed variables set in the dashboard.
 * Local development will use the same REACT_APP_ prefixed variables from .env files.
 */

interface EnvironmentConfig {
    supabaseUrl: string;
    supabaseAnonKey: string;
    isDevelopment: boolean;
    isProduction: boolean;
    envName: string;
}

interface TableConfig {
    profiles: string;
    // Add other table names as needed
}

/**
 * Get environment variables with fallback support
 * Both CloudFlare Pages and Create React App use process.env at build time
 */
function getEnvironmentVariable(key: string, defaultValue = ''): string {
    // Access environment variable from process.env
    // CloudFlare Pages and Create React App both inject these at build time
    return process.env[key] || defaultValue;
}

/**
 * Detect if running on dev site
 * CloudFlare Pages sets NODE_ENV=production for all deployments,
 * so we need to check the URL or a custom env variable
 */
function isDevEnvironment(): boolean {
    // Method 1: Check if there's a custom REACT_APP_ENV variable
    if (process.env.REACT_APP_ENV) {
        return process.env.REACT_APP_ENV === 'development';
    }

    // Method 2: Check the hostname (works in browser)
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        return hostname.includes('dev.') || hostname === 'localhost';
    }

    // Method 3: Fallback to NODE_ENV (for local development)
    return process.env.NODE_ENV === 'development';
}

/**
 * Environment configuration
 * Uses REACT_APP_ prefixed variables for both local and CloudFlare deployment
 */
export const env: EnvironmentConfig = {
    supabaseUrl: getEnvironmentVariable('REACT_APP_SUPABASE_URL'),
    supabaseAnonKey: getEnvironmentVariable('REACT_APP_SUPABASE_ANON_KEY'),
    isDevelopment: isDevEnvironment(),
    isProduction: !isDevEnvironment(),
    envName: isDevEnvironment() ? 'Development' : 'Production',
};

/**
 * Table names configuration
 * Uses D_ prefix for development, P_ prefix for production
 */
export const tables: TableConfig = {
    profiles: env.isProduction ? 'P_profiles' : 'D_profiles',
    // Add other tables as needed
};

// Debug logging for environment detection
if (typeof window !== 'undefined') {
    console.log('Environment Detection Debug:');
    console.log('- Hostname:', window.location.hostname);
    console.log('- REACT_APP_ENV:', process.env.REACT_APP_ENV || 'not set');
    console.log('- NODE_ENV:', process.env.NODE_ENV);
    console.log('- Detected as:', env.envName);
    console.log('- Using table prefix:', env.isProduction ? 'P_' : 'D_');
}

// Export config as alias for env (backward compatibility)
export const config = env;

/**
 * Validate required environment variables
 */
export function validateEnvironment(): void {
    const required = {
        'Supabase URL': env.supabaseUrl,
        'Supabase Anon Key': env.supabaseAnonKey,
    };

    const missing: string[] = [];

    for (const [name, value] of Object.entries(required)) {
        if (!value) {
            missing.push(name);
        }
    }

    if (missing.length > 0) {
        console.error('Missing required environment variables:', missing.join(', '));
        console.error('Please configure the following:');
        console.error('- For local development: Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env');
        console.error('- For CloudFlare Pages: Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in CloudFlare dashboard');
    }
}

// Validate on module load
validateEnvironment();

export default env;
